import React from 'react';
import { useCurrency } from '../../contexts/CurrencyContext';

const SummaryCard = ({ title, value, subtitle, valueClassName }) => (
  <div className="bg-white dark:bg-[#151934] rounded-lg shadow border border-gray-200 dark:border-gray-700/50 hover:shadow-md transition-shadow">
    <div className="flex flex-col h-full p-6">
      <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
        {title}
      </div>
      <div className={`text-2xl font-bold ${valueClassName || 'text-gray-900 dark:text-white'} mb-1`}>
        {value}
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400 mt-auto">
        {subtitle}
      </div>
    </div>
  </div>
);

const SummaryCards = ({ portfolioData }) => {
  const { convertAmount, formatAmount, selectedCurrency } = useCurrency();

  const calculateTotalValue = (items) => {
    return items.reduce((sum, item) => {
      // Handle items with subcategories (main categories)
      if (item.subCategories) {
        const subCategoryTotal = item.subCategories.reduce((subSum, subCategory) => {
          return subSum + calculateTotalValue(subCategory.items || []);
        }, 0);
        const itemsTotal = calculateTotalValue(item.items || []);
        return sum + subCategoryTotal + itemsTotal;
      }

      // Handle items array
      if (item.items) {
        return sum + calculateTotalValue(item.items);
      }
      
      // Handle currency positions
      if (item.symbol?.endsWith('CURRENCY')) {
        const currencyCode = item.symbol.split('.')[0];
        const position = parseFloat(item.position) || parseFloat(item.quantity) || 0;
        return sum + convertAmount(position, currencyCode, selectedCurrency);
      }
      
      // Handle regular assets
      return sum + convertAmount(item.value || 0, item.currency || 'USD', selectedCurrency);
    }, 0);
  };

  const totalValue = calculateTotalValue(portfolioData?.portfolioItems || []);
  const totalProfit = convertAmount(portfolioData?.totalProfit || 0, 'USD', selectedCurrency);
  const irr = portfolioData?.irr || 0;
  const passiveIncome = portfolioData?.passiveIncome || 0;

  return (
    <>
      <SummaryCard
        title="Value"
        value={formatAmount(totalValue, selectedCurrency)}
        subtitle={`Invested: ${formatAmount(totalValue, selectedCurrency)}`}
      />

      <SummaryCard
        title="Total profit"
        value={formatAmount(totalProfit, selectedCurrency)}
        subtitle={`${((totalProfit / totalValue) * 100).toFixed(2)}% daily`}
        valueClassName={
          totalProfit > 0 ? 'text-green-600 dark:text-green-400' : 
          totalProfit < 0 ? 'text-red-600 dark:text-red-400' : 
          'text-gray-900 dark:text-white'
        }
      />

      <SummaryCard
        title="IRR"
        value={`${irr.toFixed(2)}%`}
        subtitle="Current holdings"
        valueClassName={
          irr > 0 ? 'text-green-600 dark:text-green-400' : 
          irr < 0 ? 'text-red-600 dark:text-red-400' : 
          'text-gray-900 dark:text-white'
        }
      />

      <SummaryCard
        title="Passive income"
        value={`${passiveIncome.toFixed(2)}%`}
        subtitle={`Annually: ${formatAmount((passiveIncome / 100) * totalValue, selectedCurrency)}`}
        valueClassName="text-green-600 dark:text-green-400"
      />
    </>
  );
};

export default SummaryCards;
